import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Calendar, Clock, Share2, Twitter, Linkedin, Bookmark, Heart, Upload, FileType, AlertCircle, Download, Loader, HelpCircle, Shield, Sun, Moon, User, LogOut, MessageSquare, Settings } from 'lucide-react';
import { getPostBySlug } from '../utils/blogUtils';
import Footer from './Footer';
import '../styles/blog.css';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

// Custom components for markdown rendering
const MarkdownComponents = {
  h1: props => (
    <h1 
      className="font-mono text-3xl font-bold mb-6 text-gray-200 dark:text-gray-200 mt-8" 
      {...props} 
    />
  ),
  h2: props => (
    <h2 
      className="font-mono text-2xl font-bold mb-4 mt-8 text-gray-200 dark:text-gray-200" 
      {...props} 
    />
  ),
  h3: props => (
    <h3 
      className="font-mono text-xl font-bold mb-3 mt-6 text-gray-200 dark:text-gray-200" 
      {...props} 
    />
  ),
  p: props => (
    <p 
      className="font-mono mb-4 text-gray-200 dark:text-gray-200 whitespace-pre-wrap" 
      {...props} 
    />
  ),
  ul: props => (
    <ul 
      className="font-mono list-none pl-6 mb-4 text-gray-200 dark:text-gray-200" 
      {...props} 
    />
  ),
  ol: props => (
    <ol 
      className="font-mono list-decimal pl-6 mb-4 text-gray-200 dark:text-gray-200" 
      {...props} 
    />
  ),
  li: props => (
    <li 
      className="font-mono mb-2 text-gray-200 dark:text-gray-200 before:content-['-'] before:mr-2" 
      {...props} 
    />
  ),
  strong: props => (
    <strong 
      className="font-mono font-bold text-gray-200 dark:text-gray-200" 
      {...props} 
    />
  ),
  em: props => (
    <em 
      className="font-mono italic text-gray-200 dark:text-gray-200" 
      {...props} 
    />
  ),
  blockquote: props => (
    <blockquote 
      className="border-l-4 border-blue-500 pl-4 my-4 italic text-gray-700 dark:text-gray-300" 
      {...props} 
    />
  ),
  code: props => (
    <code 
      className="font-mono bg-gray-800 dark:bg-gray-800 rounded px-2 py-1 text-sm text-gray-200 dark:text-gray-200" 
      {...props} 
    />
  ),
  pre: props => (
    <pre 
      className="font-mono bg-gray-800 dark:bg-gray-800 rounded p-4 mb-4 overflow-x-auto text-gray-200 dark:text-gray-200" 
      {...props} 
    />
  ),
  a: props => (
    <a 
      className="font-mono text-blue-400 hover:text-blue-300 dark:text-blue-400 dark:hover:text-blue-300 underline" 
      {...props}
      target="_blank"
      rel="noopener noreferrer"
    />
  ),
};

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [likes, setLikes] = useState(0);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const loadPost = async () => {
      try {
        const postData = await getPostBySlug(slug);
        setPost(postData);
      } catch (error) {
        console.error('Error loading post:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-pulse text-gray-800 dark:text-gray-200">Loading...</div>
      </div>
    );
  }

  if (!post) return null;
  const { frontMatter, content } = post;

  // Structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": frontMatter.title,
    "description": frontMatter.excerpt,
    "image": frontMatter.image,
    "datePublished": frontMatter.date,
    "author": {
      "@type": "Person",
      "name": frontMatter.author.name
    },
    "publisher": {
      "@type": "Organization",
      "name": "Predica.AI",
      "logo": {
        "@type": "ImageObject",
        "url": "https://predica.ai/logo.png"
      }
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <Helmet>
        <title>{frontMatter.title} | Predica.AI Blog</title>
        <meta name="description" content={frontMatter.excerpt} />
        <meta name="keywords" content={frontMatter.tags.join(', ')} />
        <link rel="canonical" href={`https://predica.ai/blog/${slug}`} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={frontMatter.title} />
        <meta property="og:description" content={frontMatter.excerpt} />
        <meta property="og:image" content={frontMatter.image} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={frontMatter.title} />
        <meta name="twitter:description" content={frontMatter.excerpt} />
        <meta name="twitter:image" content={frontMatter.image} />

        {/* Article Specific */}
        <meta property="article:published_time" content={frontMatter.date} />
        <meta property="article:author" content={frontMatter.author.name} />
        {frontMatter.tags.map(tag => (
          <meta property="article:tag" content={tag} key={tag} />
        ))}

        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-gray-200 dark:border-gray-800 bg-white/75 dark:bg-gray-900/75 backdrop-blur-lg">
        <div className="flex h-16 items-center px-4 sm:px-8">
          <div className="flex items-center space-x-4">
            <Link to="/" className="flex items-center space-x-2">
              <Shield className="h-8 w-8 text-blue-500" />
              <span className="text-xl font-bold text-gray-900 dark:text-white">Predica.AI</span>
            </Link>
          </div>

          <div className="ml-auto flex items-center space-x-4">
            <button
              onClick={toggleTheme}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              {theme === 'dark' ? (
                <Sun className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              ) : (
                <Moon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              )}
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="relative rounded-full overflow-hidden hover:ring-2 hover:ring-blue-500/20 transition-all duration-300">
                  <User className="h-8 w-8 text-gray-600 dark:text-gray-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem>
                  <Settings className="mr-2 h-4 w-4" />
                  <span>Settings</span>
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Logout</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Floating Share Bar */}
      <div className="fixed left-8 top-1/3 hidden lg:flex flex-col gap-4">
        <button onClick={() => setLikes(l => l + 1)} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
          <Heart className={`w-6 h-6 ${likes > 0 ? 'text-red-500 fill-red-500' : 'text-gray-500 dark:text-gray-400'}`} />
          {likes > 0 && <span className="text-sm text-gray-500 dark:text-gray-400">{likes}</span>}
        </button>
        <button className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
          <Twitter className="w-6 h-6 text-gray-500 dark:text-gray-400" />
        </button>
        <button className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
          <Linkedin className="w-6 h-6 text-gray-500 dark:text-gray-400" />
        </button>
        <button 
          onClick={() => setIsBookmarked(!isBookmarked)}
          className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <Bookmark 
            className={`w-6 h-6 ${isBookmarked ? 'text-yellow-500 fill-yellow-500' : 'text-gray-500 dark:text-gray-400'}`} 
          />
        </button>
      </div>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Author and Meta Info */}
        <div className="flex items-center mb-8">
          <img
            src={frontMatter.author.avatar}
            alt={frontMatter.author.name}
            className="w-12 h-12 rounded-full mr-4"
          />
          <div>
            <div className="font-medium text-gray-900 dark:text-white">
              {frontMatter.author.name}
            </div>
            <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 space-x-4">
              <span>{frontMatter.author.bio}</span>
              <span>·</span>
              <span>{new Date(frontMatter.date).toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric' 
              })}</span>
              <span>·</span>
              <span>{frontMatter.readTime}</span>
            </div>
          </div>
        </div>

        {/* Title and Excerpt */}
        <h1 className="text-4xl sm:text-5xl font-serif font-bold mb-4 text-gray-900 dark:text-white">
          {frontMatter.title}
        </h1>
        <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 font-serif">
          {frontMatter.excerpt}
        </p>

        {/* Featured Image */}
        <div className="mb-12">
          <img
            src={frontMatter.image}
            alt={frontMatter.title}
            className="w-full h-[400px] object-cover rounded-lg"
          />
        </div>

        {/* Content */}
        <div className="prose prose-lg dark:prose-invert max-w-none">
          <div 
            className="blog-content"
            dangerouslySetInnerHTML={{ __html: content }}
          />
        </div>

        {/* Tags */}
        <div className="mt-12 mb-8">
          <div className="flex flex-wrap gap-2">
            {frontMatter.tags.map(tag => (
              <span
                key={tag}
                className="px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800 
                  text-gray-700 dark:text-gray-300 text-sm hover:bg-gray-200 dark:hover:bg-gray-700
                  cursor-pointer transition-colors"
              >
                #{tag}
              </span>
            ))}
          </div>
        </div>

        {/* Author Bio Card */}
        <div className="mt-16 p-8 bg-gray-50 dark:bg-gray-800 rounded-xl">
          <div className="flex items-center">
            <img
              src={frontMatter.author.avatar}
              alt={frontMatter.author.name}
              className="w-16 h-16 rounded-full mr-6"
            />
            <div>
              <h3 className="font-medium text-lg text-gray-900 dark:text-white mb-2">
                Written by {frontMatter.author.name}
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                {frontMatter.author.bio}
              </p>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default BlogPost; 